import { useEffect, useRef, useState } from 'react';
import useApiClient from '../api/useApiClient';
import LocationHelpers from '../../services/helpers/LocationHelpers';

export const TimezoneInference = {
  Idle: 'idle',
  Loading: 'loading',
  Resolved: 'resolved',
  Failed: 'failed',
};

// Long enough that typing a street name does not fire a lookup per keystroke,
// short enough that the field is filled before the operator reaches for Next.
const DEBOUNCE_MS = 800;

/**
 * Infers the location's timezone from the address the operator is entering, and
 * hands it back through `onInfer` so the caller can pre-fill its field
 * (PROD-489). Never applies anything itself — the operator confirms.
 *
 * Only fires on address changes, which is what keeps a zone they picked by hand
 * from being overwritten: choosing one does not re-run the effect. A later
 * failed lookup will not clear that pick either — only a value this hook put
 * there can be cleared.
 *
 * The country and state are sent too, and usually settle the answer without a
 * point lookup at all. Coordinates come from the Google Place when one was
 * selected, and from geocoding the typed address otherwise; a region that needs
 * them and has none falls back to its own zone. A null result means nothing
 * could answer and the caller must let the operator pick.
 *
 * @param {{address: ?string, city: ?string, zipCode: ?string, country: ?string, state: ?string, timezone: ?string, lat: ?number, lon: ?number}} location
 * @param {(timezone: ?string) => void} onInfer
 * @returns {string} one of TimezoneInference
 */
export default function useInferredTimezone({ address, city, zipCode, country, state, timezone, lat, lon }, onInfer) {
  const { CompaniesManager } = useApiClient();

  const [status, setStatus] = useState(TimezoneInference.Idle);

  // Guards against an earlier, slower lookup landing after a later one and
  // pre-filling the field with the previous address's zone.
  const latestRequest = useRef(0);

  // Held in refs, not read from the effect's closure: neither the callback nor
  // the manager should retrigger inference, and a caller whose useApiClient()
  // is not memoised would otherwise loop the effect forever.
  const onInferRef = useRef(onInfer);
  onInferRef.current = onInfer;
  const companiesRef = useRef(CompaniesManager);
  companiesRef.current = CompaniesManager;

  // The field's current value, and the last value this hook put there. A failed
  // lookup may clear the field only when those agree — otherwise it would undo
  // a zone the operator chose by hand, which fixing a typo in the zip is enough
  // to trigger. Refs, not dependencies: a pick must not restart inference.
  const currentRef = useRef(timezone);
  currentRef.current = timezone;
  // Starts null so an empty field — undefined or null — reads as "nothing the
  // operator chose", and the first failure is free to leave it empty.
  const lastInferredRef = useRef(null);

  const hasAddress = Boolean(address && city && country);

  useEffect(() => {
    if (!hasAddress) {
      setStatus(TimezoneInference.Idle);

      return undefined;
    }

    const request = latestRequest.current + 1;
    latestRequest.current = request;

    setStatus(TimezoneInference.Loading);

    const timer = setTimeout(async () => {
      const coordinates = await resolveCoordinates({ address, city, zipCode, country, lat, lon });

      if (request !== latestRequest.current) {
        return;
      }

      const inferred = await companiesRef.current.inferTimezone({
        country,
        state,
        lat: coordinates?.lat ?? null,
        lon: coordinates?.lon ?? null,
      });

      if (request !== latestRequest.current) {
        return;
      }

      setStatus(inferred ? TimezoneInference.Resolved : TimezoneInference.Failed);

      // Nothing came back and the field holds something this hook did not put
      // there: leave the operator's pick alone and let the warning speak.
      if (inferred === null && (currentRef.current ?? null) !== lastInferredRef.current) {
        return;
      }

      lastInferredRef.current = inferred;
      onInferRef.current(inferred);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [address, city, zipCode, country, state, lat, lon, hasAddress]);

  return status;
}

async function resolveCoordinates({ address, city, zipCode, country, lat, lon }) {
  if (lat != null && lon != null) {
    return { lat, lon };
  }

  const { success, coordinates } = await LocationHelpers.getCoordinates(
    LocationHelpers.compileAddress(address, zipCode, city, country),
  );

  return success ? { lat: coordinates.lat(), lon: coordinates.lng() } : null;
}
